import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, addDoc, updateDoc, doc, where, arrayUnion, arrayRemove, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import type { Upvote } from '../types';
import { Lightbulb, Plus, ThumbsUp, X, AlertCircle, Trash2 } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';

export default function Upvotes() {
  const { t } = useTranslation();
  const user = useStore((state) => state.user);
  const [upvotes, setUpvotes] = useState<Upvote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [upvoteToDelete, setUpvoteToDelete] = useState<Upvote | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });

  useEffect(() => {
    const fetchUpvotes = async () => {
      try {
        const q = query(
          collection(db, 'upvotes'),
          orderBy('votes', 'desc'),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const upvotesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt.toDate(),
          updatedAt: doc.data().updatedAt.toDate()
        })) as Upvote[];

        setUpvotes(upvotesData);
      } catch (err) {
        console.error('Error fetching upvotes:', err);
        setError(t('upvotes.failedToLoadRequests'));
      } finally {
        setLoading(false);
      }
    };

    fetchUpvotes();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      const upvoteData = {
        title: formData.title,
        description: formData.description,
        authorId: user.uid,
        authorName: user.displayName || user.email?.split('@')[0] || 'Anonymous',
        votes: 0,
        voters: [],
        status: 'accepted',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'upvotes'), upvoteData);

      // Refresh upvotes list
      const q = query(
        collection(db, 'upvotes'),
        orderBy('votes', 'desc'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const upvotesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate()
      })) as Upvote[];

      setUpvotes(upvotesData);
      setShowForm(false);
      setFormData({ title: '', description: '' });
    } catch (err) {
      console.error('Error adding upvote:', err);
      setError(t('upvotes.failedToAddRequest'));
    }
  };

  const handleVote = async (upvoteId: string, hasVoted: boolean) => {
    if (!user) return;

    try {
      const upvoteRef = doc(db, 'upvotes', upvoteId);
      
      await updateDoc(upvoteRef, {
        votes: hasVoted ? upvotes.find(u => u.id === upvoteId)!.votes - 1 : upvotes.find(u => u.id === upvoteId)!.votes + 1,
        voters: hasVoted ? arrayRemove(user.uid) : arrayUnion(user.uid),
        updatedAt: new Date()
      });

      // Refresh upvotes list
      const q = query(
        collection(db, 'upvotes'),
        orderBy('votes', 'desc'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const upvotesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate()
      })) as Upvote[];

      setUpvotes(upvotesData);
    } catch (err) {
      console.error('Error updating vote:', err);
      setError(t('upvotes.failedToUpdateVote'));
    }
  };

  const handleDelete = async (upvoteId: string) => {
    try {
      await deleteDoc(doc(db, 'upvotes', upvoteId));
      
      // Refresh upvotes list
      const q = query(
        collection(db, 'upvotes'),
        orderBy('votes', 'desc'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const upvotesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate()
      })) as Upvote[];

      setUpvotes(upvotesData);
      setUpvoteToDelete(null);
    } catch (err) {
      console.error('Error deleting upvote:', err);
      setError(t('upvotes.failedToDeleteRequest'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1b3968] mb-4"></div>
          <p className="text-gray-600">{t('upvotes.loadingFeatureRequests')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-4xl mx-auto px-4 py-8'>
      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8'>
        <div>
          <h1 className='text-2xl md:text-3xl font-bold text-primary-600 flex items-center gap-2 italic'>
            <Lightbulb className='h-7 w-7' />
            {t('upvotes.title')}
          </h1>
          <p className='text-gray-600 mt-2 text-sm sm:text-base'>
            {t('upvotes.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className='w-full sm:w-auto bg-[#1b3968] text-white px-4 py-2 rounded-md hover:bg-[#1b3968]/90 flex items-center justify-center gap-2'
        >
          <Plus className='h-5 w-5' />
          {t('upvotes.newRequest')}
        </button>
      </div>

      {error && (
        <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center'>
          <AlertCircle className='h-5 w-5 mr-2 flex-shrink-0' />
          <span className='text-sm'>{error}</span>
        </div>
      )}

      {showForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg w-full max-w-md p-4 sm:p-6'>
            <div className='flex justify-between items-center mb-6'>
              <h2 className='text-xl font-bold'>{t('upvotes.newFeatureRequest')}</h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setFormData({ title: '', description: '' });
                }}
                className='text-gray-400 hover:text-gray-500'
              >
                <X className='h-6 w-6' />
              </button>
            </div>

            <form onSubmit={handleSubmit} className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('upvotes.requestTitle')}
                </label>
                <input
                  type='text'
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968] text-base'
                  placeholder={t('upvotes.titlePlaceholder')}
                  required
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('upvotes.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968] text-base'
                  rows={4}
                  placeholder={t('upvotes.descriptionPlaceholder')}
                  required
                />
              </div>
              <div className='flex flex-col sm:flex-row justify-end gap-3 pt-4'>
                <button
                  type='button'
                  onClick={() => {
                    setShowForm(false);
                    setFormData({ title: '', description: '' });
                  }}
                  className='w-full sm:w-auto px-4 py-2 text-gray-700 hover:text-gray-900 text-center'
                >
                  {t('upvotes.cancel')}
                </button>
                <button
                  type='submit'
                  className='w-full sm:w-auto bg-[#1b3968] text-white px-4 py-2 rounded-md hover:bg-[#1b3968]/90'
                >
                  {t('upvotes.submitRequest')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className='space-y-4'>
        {upvotes.map((upvote) => {
          const hasVoted = user ? upvote.voters.includes(user.uid) : false;
          const isAuthor = user && user.uid === upvote.authorId;

          return (
            <div
              key={upvote.id}
              className='bg-white shadow rounded-lg p-4 sm:p-6'
            >
              <div className='flex flex-col sm:flex-row gap-4'>
                <button
                  onClick={() => handleVote(upvote.id, hasVoted)}
                  className={`flex sm:flex-col items-center gap-2 p-2 rounded-lg transition-colors ${
                    hasVoted
                      ? 'text-[#1b3968] bg-[#1b3968]/5'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                  disabled={!user}
                >
                  <ThumbsUp
                    className={`h-5 w-5 ${hasVoted ? 'fill-current' : ''}`}
                  />
                  <span className='text-sm font-medium'>{upvote.votes}</span>
                </button>
                <div className='flex-1 min-w-0'>
                  <div className='flex justify-between items-start gap-2'>
                    <h3 className='text-lg font-medium text-gray-900 break-words'>
                      {upvote.title}
                    </h3>
                    {isAuthor && (
                      <button
                        onClick={() => setUpvoteToDelete(upvote)}
                        className='text-gray-400 hover:text-red-500 p-1 flex-shrink-0'
                        title='Delete feature request'
                      >
                        <Trash2 className='h-4 w-4' />
                      </button>
                    )}
                  </div>
                  <p className='text-gray-600 mt-2 text-sm sm:text-base break-words'>
                    {upvote.description}
                  </p>
                  <div className='flex flex-wrap items-center gap-2 mt-3 text-xs sm:text-sm text-gray-500'>
                    <span>{t('upvotes.requestedBy')} {upvote.authorName}</span>
                    <span className='hidden sm:inline'>•</span>
                    <span>{upvote.createdAt.toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Delete Confirmation Modal */}
      {upvoteToDelete && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg w-full max-w-md p-4 sm:p-6'>
            <h2 className='text-xl font-bold mb-4'>{t('upvotes.deleteFeatureRequest')}</h2>
            <p className='text-gray-600 mb-6 text-sm sm:text-base'>
              {t('upvotes.deleteConfirmation')}
            </p>
            <div className='flex flex-col sm:flex-row justify-end gap-3'>
              <button
                onClick={() => setUpvoteToDelete(null)}
                className='w-full sm:w-auto px-4 py-2 text-gray-600 hover:text-gray-900 text-center'
              >
                {t('upvotes.cancel')}
              </button>
              <button
                onClick={() => handleDelete(upvoteToDelete.id)}
                className='w-full sm:w-auto bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600'
              >
                {t('upvotes.deleteRequest')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}